// Hooks
import { defineStore } from "pinia";
import { computed, reactive, ref } from "vue";

// Types
import type { I_it4CreateContract_Return } from "@/hooks/requests/it4CreateContract/it4CreateContract.types";

import type { I_Company_Field, I_Individual_Field } from "./successfulEvaluation.types";

/**
 * 🆂🆃🅾🆁🅴 [Successful Evaluation]
 */
export const useSuccessfulEvaluationStore = defineStore("successfulEvaluation", () => {
    // |-------|
    // | STATE |
    // |-------|

    const formType = ref<"individual" | "company">("individual");

    const companyFields = reactive<I_Company_Field>({
        companyName: "",
        name: "",
        surname: "",
        identificationNumber: "",
        passportNumber: "",
        NIP: "",
        REGON: "",
        KRS: "",
        postalCode: "",
        street: "",
        city: "",
        houseNumber: "",
        apartmentNumber: "",
        email: "",
        phone: "",
        owner: false,
        VATPayer: false,
        personal: false,
        duty: false,
    });

    const individualFields = reactive<I_Individual_Field>({
        PESEL: "",
        name: "",
        surname: "",
        idNumber: "",
        dateOfIssue: "",
        postalCode: "",
        street: "",
        houseNumber: "",
        city: "",
        apartmentNumber: "",
        email: "",
        phone: "",
        personal: false,
        duty: false,
    });

    const contractData = ref<null | I_it4CreateContract_Return["data"]>(null);

    // |----------|
    // | COMPUTED |
    // |----------|

    const isAcceptedToNext = computed<boolean>(() => {
        if (formType.value === "company") {
            let key: keyof I_Company_Field;
            for (key in companyFields) {
                if (Object.prototype.hasOwnProperty.call(companyFields, key)) {
                    if (key === "apartmentNumber" || key === "KRS") continue;
                    if (companyFields[key]) continue;
                    return false;
                }
            }
        } else {
            let key: keyof I_Individual_Field;
            for (key in individualFields) {
                if (Object.prototype.hasOwnProperty.call(individualFields, key)) {
                    if (key === "apartmentNumber") continue;
                    if (individualFields[key]) continue;
                    return false;
                }
            }
        }

        return true;
    });

    // |---------|
    // | METHODS |
    // |---------|

    const restoreToDefault = () => {
        contractData.value = null;
        let individualKey: keyof I_Individual_Field;
        let companyKey: keyof I_Company_Field;

        for (companyKey in companyFields) {
            if (Object.prototype.hasOwnProperty.call(companyFields, companyKey)) {
                if (
                    companyKey === "personal" ||
                    companyKey === "duty" ||
                    companyKey === "VATPayer" ||
                    companyKey === "owner"
                ) {
                    companyFields[companyKey] = false;
                    continue;
                }
                companyFields[companyKey] = "";
            }
        }
        for (individualKey in individualFields) {
            if (Object.prototype.hasOwnProperty.call(individualFields, individualKey)) {
                if (individualKey === "personal" || individualKey === "duty") {
                    individualFields[individualKey] = false;
                    continue;
                }
                individualFields[individualKey] = "";
            }
        }
    };

    const address = computed(() => {
        const fields = formType.value === "individual" ? individualFields : companyFields;
        return `${fields.street} ${fields.houseNumber}, apt. ${fields.apartmentNumber}`.trim();
    });

    return {
        // * 🆂🆃🅾🆁🅴
        individualFields,
        contractData,
        formType,
        companyFields,
        address,

        // * 🅲🅾🅼🅿🆄🆃🅴🅳
        isAcceptedToNext,

        // * 🅼🅴🆃🅷🅾🅳🆂
        restoreToDefault,
    };
});
